import React, { useEffect, useState } from 'react';
import { Section, Container, Card } from '../common';
import { Check } from 'lucide-react';
import { BACKOFFICE_API_URL } from '../../config/api';

const fallbackPlans = [
  {
    id: 'starter-fallback',
    name: 'Starter',
    price: '99,00',
    period: '/mês',
    description: 'Para operações que estão começando a vender pelo próprio canal.',
    features: ['Cardápio digital', 'Pedidos online', 'Cupons', 'Dashboard'],
    cta: 'Começar agora',
    popular: false,
    requiresCommercialContact: false
  },
  {
    id: 'growth-fallback',
    name: 'Growth',
    price: '199,00',
    period: '/mês',
    description: 'Para restaurantes que precisam integrar operação, atendimento e gestão.',
    features: ['Tudo do Starter', 'WhatsApp', 'Promoções', 'Financeiro', 'KDS'],
    cta: 'Começar agora',
    popular: true,
    requiresCommercialContact: false
  },
  {
    id: 'enterprise-fallback',
    name: 'Enterprise',
    price: 'Sob consulta',
    customPrice: true,
    period: '',
    description: 'Para redes e operações com necessidades comerciais e suporte dedicado.',
    features: ['Recursos avançados', 'Integrações', 'Implantação assistida', 'Suporte prioritário'],
    cta: 'Falar com consultor',
    popular: false,
    requiresCommercialContact: true
  }
];

const featureLabels = {
  DIGITAL_MENU: 'Cardápio digital',
  ONLINE_ORDERING: 'Pedidos online',
  COUPONS: 'Cupons',
  PROMOTIONS: 'Promoções',
  COMBOS: 'Combos',
  CASHBACK: 'Cashback',
  CRM: 'CRM de clientes',
  REVIEWS: 'Avaliações',
  SALES_RECOVERY: 'Recuperador de vendas',
  CUSTOMER_BLOCK: 'Bloqueio de clientes',
  WHATSAPP: 'WhatsApp',
  POS: 'POS',
  TABLES: 'Mesas e comandas',
  KDS: 'KDS',
  DELIVERY: 'Delivery',
  IFOOD: 'iFood',
  STOCK: 'Estoque',
  RECIPES: 'Ficha técnica',
  FINANCE: 'Financeiro',
  REPORTS: 'Relatórios',
  DASHBOARD: 'Dashboard',
  PIX_ONLINE: 'Pix online'
};

const formatFeature = (feature) => featureLabels[feature] || String(feature).replace(/_/g, ' ');

const Pricing = () => {
  const [plans, setPlans] = useState(fallbackPlans);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetch(`${BACKOFFICE_API_URL}/v1/public/plans`)
      .then((response) => {
        if (!response.ok) throw new Error('plans_unavailable');
        return response.json();
      })
      .then((items) => {
        if (!mounted || !Array.isArray(items) || items.length === 0) return;
        setPlans(items.map((item) => ({
          id: item.id,
          name: item.name,
          oldPrice: item.promotionActive && item.promotionalPrice
            ? `R$ ${Number(item.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : undefined,
          price: Number((item.promotionActive && item.promotionalPrice) ? item.promotionalPrice : item.price || 0)
            .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          period: item.billingPeriod === 'YEARLY' ? '/ano' : item.billingPeriod === 'QUARTERLY' ? '/trimestre' : '/mês',
          description: item.description,
          features: (item.features || []).map(formatFeature),
          cta: item.ctaLabel || (item.requiresCommercialContact ? 'Falar com consultor' : 'Começar agora'),
          popular: Boolean(item.highlighted),
          requiresCommercialContact: Boolean(item.requiresCommercialContact),
          promotionLabel: item.promotionLabel,
          promotionDurationMonths: item.promotionDurationMonths
        })));
      })
      .catch(() => {
        if (mounted) setPlans(fallbackPlans);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Section id="planos" className="bg-white">
      <Container>
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="fw-bold">Nossos Planos</h2>
          <p className="text-secondary lead">Escolha o plano ideal para transformar a gestão do seu restaurante</p>
        </div>
        <div className="row g-4 justify-content-center align-items-stretch">
          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary-custom" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="mt-3 text-secondary">Carregando planos...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="col-12 text-center py-5">
              <p className="text-secondary">Nenhum plano disponível no momento.</p>
            </div>
          ) : (
            plans.map((plan, index) => (
              <div key={plan.id || plan.name || index} className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay={index * 150}>
                <Card className={`h-100 text-center p-4 p-lg-5 ${plan.popular ? 'border-primary-custom border-2 shadow-lg scale-up' : 'border-light shadow-sm'}`}>
                  {plan.popular && (
                    <div className="position-absolute top-0 start-50 translate-middle">
                      <span className="badge bg-primary-custom text-black px-4 py-2 rounded-pill shadow-sm">Mais Escolhido</span>
                    </div>
                  )}
                  <div className="mt-2">
                    <div className="mb-3">
                      <h3 className="h4 fw-bold mb-2">{plan.name}</h3>
                      <p className="text-secondary small mb-0 mx-auto" style={{ maxWidth: '200px' }}>{plan.description}</p>
                    </div>
                    <div className="mb-2">
                      {plan.oldPrice && (
                        <div className="text-secondary text-decoration-line-through small mb-0">{plan.oldPrice}</div>
                      )}
                      {plan.customPrice ? (
                        <div className="d-flex align-items-baseline justify-content-center mb-1">
                          <span className="h2 fw-bold mb-0 text-dark">{plan.price}</span>
                        </div>
                      ) : (
                        <div className="d-flex align-items-baseline justify-content-center mb-1">
                          <span className="h4 fw-bold mb-0 me-1">R$</span>
                          <span className="display-4 fw-bold mb-0 text-dark">{plan.price}</span>
                          <span className="text-secondary small ms-1">{plan.period}</span>
                        </div>
                      )}
                      {(plan.promotionLabel || plan.promotionDurationMonths) && (
                        <div className="small fw-semibold text-success">
                          {plan.promotionLabel || `Preço promocional por ${plan.promotionDurationMonths} meses`}
                        </div>
                      )}
                    </div>
                    <hr className="my-4 opacity-10" />
                  </div>
                  <ul 
                    className="list-unstyled mb-5 text-start flex-grow-1 overflow-auto"
                    style={{ maxHeight: '400px', overflowX: 'hidden' }}
                  >
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="mb-1 d-flex align-items-start">
                        <Check size={18} className="text-success me-2 mt-1 flex-shrink-0" />
                        <span className="text-secondary small">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a 
                    href={plan.requiresCommercialContact ? '#contato' : `/cadastro${plan.id ? `?planId=${encodeURIComponent(plan.id)}` : ''}`}
                    className={`btn w-100 mt-auto py-3 fw-bold ${plan.popular ? 'btn-primary-custom shadow-sm' : 'btn-outline-custom'}`}
                  >
                    {plan.cta}
                  </a>
                </Card>
              </div>
            ))
          )}
        </div>
      </Container>
    </Section>
  );
};

export default Pricing;
